// A PO's Documents tab: the vendor documents link (copy, open, regenerate, revoke) and when it expires, each
// line's spec revision with its files and whether the vendor confirmed having it, what the vendor's page shows
// (the PO as last marked as sent) and what waits for the next send, and who viewed or downloaded what. The link's token isn't synced, so the link (and the log's addresses) come from /api/po/:id/docs.
// Packaging --- packaging lines come last under their own heading, each with what it is and a warning when its revision
// lacks what the vendor prints from; every file shows its kind and downloads.
// Product labels --- a line whose product carries HHL product labels lists them (the vendor downloads them from its
// page, made by HHL Ops at the label's size), each with a warn chip when something's off; a line that is one downloads
// every product's label; the log says which line's labels the vendor downloaded (doc_access.po_line_id; none: the PDF
// of every product label on the order).
// Make POs --- under a build, the parts its CM makes or buys for it whose documents go with it (a PCBA's Gerbers, a box's
// print files), each with its revision, files, gaps and confirmation like a line; parts with no spec and nothing to
// print aren't listed, but a sub-assembly without one is (the CM has nothing to build it from).
import { AlertTriangle, CircleCheck, Copy, Download, ExternalLink, Eye, Link2, Link2Off, Package, RefreshCw, Tag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DocAccess, Po, PoLine, Vendor } from "../../../../shared/db.gen.ts";
import { DOC_LINK_DAYS, docsPath, fileSize, type LinkState, linkState } from "../../../../shared/po-docs.ts";
import { SPEC_FILE_KIND_LABELS } from "../../../../shared/specs.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { toast } from "../../ui/toast.tsx";
import { labelPartUrl, productLabelsUrl } from "../PackagingSection.tsx";
import { downloadUrl } from "../specs/lib.ts";
import { type LineFile, type LineSpec, type PartLineSpec, useLastSent, usePoAccess, when } from "./docs.ts";
import { vendorLabel } from "./lib.ts";
import { LabelWarnChip } from "./PoLines.tsx";
import { fixTitle, gapSentence, type LineLabels, type LinePacking, noFilesNote, sizeNote, useOpenFix } from "./packaging.ts";

/** A row of the Lines table: a PO line, or (Make POs) a part of a build whose documents go with it. */
type DocRow = {
  id: string;
  line: PoLine;
  part: PartLineSpec | null;
  /** The build it's grouped under (one with parts). */
  group: string | null;
};

/** Packaging --- the band packaging lines sit in, after the others. */
const PACKAGING: TableSection = {
  key: "packaging",
  label: (
    <span className="flex items-center gap-1.5">
      <Package className="size-3.5" /> Packaging
    </span>
  ),
  order: 1,
};

type Details = { link: { id: string; path: string; state: LinkState } | null; access: { id: string; ip: string; user_agent: string }[] };

const STATE_LABEL: Record<LinkState, [string, "ok" | "danger" | "neutral"]> = {
  live: ["Live", "ok"],
  revoked: ["Turned off", "danger"],
  expired: ["Expired", "neutral"],
};

/** "Chrome on Android", "WeChat on iPhone": enough to tell people apart, not the whole user agent. */
function browser(ua: string) {
  if (!ua) return "";
  const app = /MicroMessenger|WeChat/i.test(ua)
    ? "WeChat"
    : /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : /Safari\//.test(ua)
            ? "Safari"
            : /curl|wget|python|aria2|Thunder|Xunlei/i.test(ua)
              ? "a download tool"
              : "a browser";
  const os = /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "Mac" : "";
  return os ? `${app} on ${os}` : app;
}

export function PoDocuments({
  po,
  lines,
  specs,
  vendor,
  packing,
  partPacking,
  labels,
}: {
  po: Po;
  lines: PoLine[];
  specs: Map<string, LineSpec>;
  vendor: Vendor | undefined;
  /** Packaging --- the packaging lines (usePoPacking()). */
  packing: Map<string, LinePacking>;
  /** Make POs --- a build's packaging parts, by `${line id}:${part id}` (usePoPacking()). */
  partPacking: Map<string, LinePacking>;
  /** Product labels --- the lines with labels (usePoLabels()). */
  labels: Map<string, LineLabels>;
}) {
  const openFix = useOpenFix();
  const links = useRows("doc_link");
  const versions = useRows("file_version");
  const revisions = useRows("spec_revision");
  const access = usePoAccess(po.id);
  const sent = useLastSent(po.id);
  const removed = sent ? sent.lines.filter((sl) => !lines.some((l) => l.id === sl.id)).length : 0;
  const live = links.find((l) => l.po_id === po.id && !l.revoked_at);
  const newest = live ?? links.filter((l) => l.po_id === po.id).sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const [details, setDetails] = useState<Details | null>(null);

  // Fetched once, then again when the link changes or someone uses it (both arrive by sync).
  const stamp = `${newest?.id ?? ""}${newest?.updated_at ?? ""}${access.length}`;
  useEffect(() => {
    let current = true;
    if (stamp === "") return;
    fetch(`/api/po/${po.id}/docs`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Details | null) => current && setDetails(d));
    return () => {
      current = false;
    };
  }, [po.id, stamp]);

  const url = details?.link?.state === "live" && details.link.id === live?.id ? `${location.origin}${details.link.path}` : null;
  const state = newest ? linkState(newest) : null;
  const who = vendorLabel(vendor);
  const byId = useMemo(() => new Map(details?.access.map((a) => [a.id, a]) ?? []), [details]);
  const fileName = (id: string | null) => versions.find((v) => v.id === id)?.filename ?? "a file";
  const packed = lines.filter((l) => packing.has(l.id)); // Packaging --- listed last, under their heading
  const revName = (id: string | null) => revisions.find((r) => r.id === id)?.rev ?? "?";
  // Product labels --- which line's labels a download was: "downloaded GCU1300's label" (a line taken off since: its SKU
  // as last sent); none: every product label on the order in one PDF.
  const labelDownload = (a: DocAccess) => {
    if (!a.po_line_id) return "downloaded every product label on the order";
    const line = lines.find((l) => l.id === a.po_line_id);
    const sku = line ? line.sku || line.name : sent?.lines.find((l) => l.id === a.po_line_id)?.sku;
    const lab = labels.get(a.po_line_id);
    const one = lab?.kind === "product" && lab.labels.length === 1;
    return sku ? `downloaded ${sku}'s ${one ? "label" : "labels"}` : "downloaded a line's labels";
  };

  const regenerate = async () => {
    if (live && !confirm("Make a new link? The current one stops working, including the QR code on PDFs already sent.")) return;
    const { ok, result } = await mutateWithResult("doc_link.regenerate", { po_id: po.id });
    if (ok) {
      const token = (result as { token: string }).token;
      await navigator.clipboard?.writeText(`${location.origin}${docsPath(token)}`).catch(() => {});
      toast.ok("New link made and copied. Send the vendor the new PDF or link.");
    }
  };
  const revoke = async () => {
    if (!confirm(`Turn off the documents link? ${who} won't be able to open it or download files.`)) return;
    if ((await mutateWithResult("doc_link.revoke", { po_id: po.id })).ok) toast.ok("Link turned off");
  };
  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast.ok("Link copied");
  };

  /** The parts worth a row: with a released spec, packaging, or a sub-assembly (which needs one). */
  const shownParts = (l: PoLine) => (specs.get(l.id)?.parts ?? []).filter((x) => x.revision || x.subassembly || partPacking.has(`${l.id}:${x.itemId}`));

  // The lines as the usual table: packaging lines last in their band; Make POs --- a build with parts whose documents go
  // with it is a group (the build's own row, then each part), opened by its header.
  const rows: DocRow[] = [...lines.filter((l) => !packing.has(l.id)), ...packed].flatMap((l) => {
    const parts = shownParts(l);
    const group = parts.length ? l.id : null;
    return [{ id: l.id, line: l, part: null, group }, ...parts.map((part) => ({ id: `${l.id}:${part.itemId}`, line: l, part, group }))];
  });
  const hasParts = rows.some((r) => r.part);
  /** A row's spec (the line's, or the build part's own), packaging and labels. */
  const info = (r: DocRow) => {
    const lab = r.part ? undefined : labels.get(r.line.id);
    return {
      s: (r.part ?? specs.get(r.line.id)) as Omit<LineSpec, "parts"> | undefined,
      p: r.part ? partPacking.get(`${r.line.id}:${r.part.itemId}`) : packing.get(r.line.id),
      lab,
      labPart: lab?.kind === "part" ? lab : null,
    };
  };
  const pageNote = (s: Omit<LineSpec, "parts"> | undefined) =>
    po.sent_revision !== null && s && !s.onPage
      ? `Not on ${who}'s page yet`
      : s?.onPage && s.vendorRevision?.id !== s.revision?.id
        ? `${who}'s page: ${s.vendorRevision ? `Rev ${s.vendorRevision.rev}` : "no spec"}`
        : "";
  const specText = (r: DocRow) => {
    const { s, p } = info(r);
    return s?.revision ? `Rev ${s.revision.rev}` : !r.part && p && !p.pack.spec ? "No spec needed" : "No spec";
  };
  const gapButton = (p: LinePacking) => (
    <button
      type="button"
      className="flex min-w-0 items-center gap-1 text-left text-warn hover:underline"
      title={`${gapSentence(p)}\n\n${fixTitle(p)}`}
      onClick={(e) => {
        e.stopPropagation();
        openFix(p);
      }}
    >
      <AlertTriangle className="size-3.5 shrink-0" /> <span className="truncate">{gapSentence(p)}</span>
    </button>
  );
  /** A row's files: one downloads from the cell; more open a list by the button (each downloads). */
  const fileLinks = (files: LineFile[]) =>
    files.length === 1 ? (
      <a
        href={downloadUrl(files[0].id)}
        download={files[0].filename}
        title={`Download ${files[0].filename} (${fileSize(files[0].size)})`}
        onClick={(e) => e.stopPropagation()}
        className="flex min-w-0 items-center gap-1 hover:text-accent-text hover:underline"
      >
        <Download className="size-3 shrink-0 text-faint" />
        <span className="shrink-0 text-muted">{SPEC_FILE_KIND_LABELS[files[0].kind] ?? files[0].kind}:</span>
        <span className="truncate">{files[0].filename}</span>
      </a>
    ) : files.length > 1 ? (
      <Popover
        width={420}
        button={(toggle, open) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            className={cx("inline-flex shrink-0 items-center gap-1 rounded px-1 text-accent-text hover:bg-hover", open && "bg-hover")}
            title={files.map((f) => `${SPEC_FILE_KIND_LABELS[f.kind] ?? f.kind}: ${f.filename}`).join("\n")}
          >
            <Download className="size-3" /> {files.length} files
          </button>
        )}
      >
        {() => (
          <div className="flex flex-col gap-1 text-[12px]">
            {files.map((f) => (
              <div key={f.id} className="truncate">
                <span className="text-muted">{SPEC_FILE_KIND_LABELS[f.kind] ?? f.kind}:</span>{" "}
                <a href={downloadUrl(f.id)} download={f.filename} className="hover:text-accent-text hover:underline" title="Download">
                  {f.filename}
                </a>{" "}
                <span className="text-faint">{fileSize(f.size)}</span>
              </div>
            ))}
          </div>
        )}
      </Popover>
    ) : null;
  const filesCell = (r: DocRow) => {
    const { s, p, lab, labPart } = info(r);
    if (r.part)
      return (
        <>
          {fileLinks(r.part.files)}
          {r.part.revision && !r.part.files.length && !p && <span className="text-faint">No files</span>}
          {!r.part.revision && !p && (
            <span className="truncate text-warn" title={`${who} gets nothing to build it from: release its spec`}>
              {who} gets nothing to build it from: release its spec
            </span>
          )}
          {p && p.gaps.length > 0 && gapButton(p)}
        </>
      );
    const note = p && !labPart && !p.gaps.length && !s?.files.length ? noFilesNote(p.pack) : null;
    return (
      <>
        {s?.revision && (s.files.length ? fileLinks(s.files) : !p && !lab && <span className="text-faint">No files</span>)}
        {/* Product labels --- a label part's files from before it was an HHL product label stay off the vendor's page. */}
        {labPart && s?.revision && s.files.length > 0 && (
          <span className="truncate text-faint" title={`Not sent to ${who}: it gets the labels HHL Ops makes instead`}>
            Not sent to {who}: it gets the labels HHL Ops makes instead
          </span>
        )}
        {p && p.gaps.length > 0 && gapButton(p)}
        {note && <span className="truncate text-faint">{note}</span>}
      </>
    );
  };
  const filesText = (r: DocRow) => {
    const { s, p } = info(r);
    return [...(s?.files ?? []).map((f) => f.filename), ...(p?.gaps.length ? [gapSentence(p)] : [])].join("; ");
  };
  /** Product labels --- the labels HHL Ops makes: a label part's for every product, a product's own. */
  const labelsCell = (r: DocRow) => {
    const { p, lab, labPart } = info(r);
    if (labPart && p && !p.gaps.length)
      return (
        <>
          <a
            href={labelPartUrl(labPart.item.id, { download: true })}
            onClick={(e) => e.stopPropagation()}
            className="truncate hover:text-accent-text hover:underline"
            title="HHL Ops makes them from the products: a page each"
          >
            {labPart.products ? `${labPart.products} ${labPart.products === 1 ? "product" : "products"}` : "a sample (on no product's BOM yet)"} · PDF
          </a>
          <span className="shrink-0 text-faint">{sizeNote(labPart.size)}</span>
          <LabelWarnChip warnings={labPart.warnings} label={labPart.item} />
        </>
      );
    if (lab?.kind !== "product") return null;
    const onPage = po.sent_revision !== null && specs.get(r.line.id)?.onPage;
    return lab.labels.map((x) => (
      <span key={x.item.id} className="flex shrink-0 items-center gap-1">
        <a
          href={productLabelsUrl(r.line.item_id, { label: x.item.id, download: true })}
          onClick={(e) => e.stopPropagation()}
          className="hover:text-accent-text hover:underline"
          title={`HHL Ops makes it from the product at ${sizeNote(x.size)}; ${who} downloads it from the documents page${onPage ? ` · on ${who}'s page` : ""}`}
        >
          {x.item.sku || x.item.name} · PDF
        </a>
        {lab.labels.length === 1 && (
          <span className="text-faint">
            {sizeNote(x.size)}
            {onPage ? ` · on ${who}'s page` : ""}
          </span>
        )}
        <LabelWarnChip warnings={x.warnings} label={x.item} />
      </span>
    ));
  };
  const confirmedText = (s: Omit<LineSpec, "parts"> | undefined) =>
    s?.vendorRevision ? (s.confirmedAt ? `${who} has Rev ${s.vendorRevision.rev} · ${when(s.confirmedAt)}` : "Not confirmed yet") : "";
  /** What needs fixing on a row: a packaging gap, or a part the CM has nothing to build from. */
  const needsFix = (r: DocRow) => {
    const { p } = info(r);
    return !!p?.gaps.length || (!!r.part && !r.part.revision && !p);
  };
  const lineOf = (rs: DocRow[]) => (rs.find((r) => !r.part) ?? rs[0]).line;

  const columns: Column<DocRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(130px,1fr)",
      value: (r) => (r.part ? r.part.sku || r.part.name : r.line.sku),
      cell: (r) =>
        r.part ? (
          <span className="truncate font-mono" title={r.part.name}>
            {r.part.sku || r.part.name}
          </span>
        ) : (
          <span className="truncate font-mono">{r.line.sku}</span>
        ),
      groupCell: (rs) => <span className="truncate font-mono">{lineOf(rs).sku}</span>,
    },
    {
      key: "pos",
      header: "#",
      width: "44px",
      align: "right",
      // A build's parts sort with it
      value: (r) => r.line.position,
      cell: (r) => (r.part ? null : <span className="text-faint">{r.line.position}</span>),
      groupCell: (rs) => <span className="font-normal text-faint">{lineOf(rs).position}</span>,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(140px,1.5fr)",
      value: (r) => (r.part ? r.part.name : r.line.name),
      cell: (r) => <span className="truncate text-muted">{r.part ? r.part.name : r.line.name}</span>,
      groupCell: (rs) => <span className="truncate font-normal text-muted">{lineOf(rs).name}</span>,
    },
    ...(packed.length || hasParts
      ? [
          {
            key: "what",
            header: "What",
            width: "150px",
            value: (r: DocRow) => {
              const { p } = info(r);
              return p ? p.pack.title : r.part ? "For this build" : r.group ? "The build" : "";
            },
            cell: (r: DocRow) => {
              const { p } = info(r);
              const title = p ? p.pack.says : r.part ? "The CM makes or buys it for this build, so its documents go with it" : undefined;
              return (
                <span className="truncate text-muted" title={title}>
                  {p ? p.pack.title : r.part ? "For this build" : r.group ? "The build" : ""}
                </span>
              );
            },
            groupCell: (rs: DocRow[]) => (
              <span className="truncate font-normal text-faint">
                With {rs.length - 1} {rs.length === 2 ? "part" : "parts"}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "rev",
      header: "Spec",
      width: "minmax(150px,1fr)",
      value: specText,
      cell: (r) => {
        const { s } = info(r);
        const page = pageNote(s);
        return (
          <>
            {s?.revision ? <Badge tone="accent">Rev {s.revision.rev}</Badge> : <span className="shrink-0 text-faint">{specText(r)}</span>}
            {s?.newer && <Badge tone="warn">Rev {s.newer.rev} is newer</Badge>}
            {page && (
              <span className="truncate text-faint" title={page}>
                {page}
              </span>
            )}
          </>
        );
      },
      groupCell: (rs) => {
        const s = specs.get(lineOf(rs).id);
        return s?.revision ? <Badge tone="accent">Rev {s.revision.rev}</Badge> : <span className="font-normal text-faint">No spec</span>;
      },
    },
    {
      key: "files",
      header: "Files",
      width: "minmax(170px,1.5fr)",
      value: filesText,
      cell: filesCell,
      groupCell: (rs) => {
        const n = rs.reduce((sum, r) => sum + (info(r).s?.files.length ?? 0), 0);
        const fix = rs.filter(needsFix).length;
        return (
          <span className="flex min-w-0 items-center gap-1.5 font-normal">
            <span className="text-faint">
              {n} {n === 1 ? "file" : "files"}
            </span>
            {fix > 0 && (
              <span className="inline-flex items-center gap-1 text-warn" title="Open the build to see what needs fixing">
                <AlertTriangle className="size-3.5" /> {fix}
              </span>
            )}
          </span>
        );
      },
    },
    ...(labels.size
      ? [
          {
            key: "labels",
            header: "Labels",
            width: "minmax(150px,1fr)",
            value: (r: DocRow) => {
              const { lab, labPart } = info(r);
              return labPart ? `${labPart.products} products` : lab?.kind === "product" ? lab.labels.map((x) => x.item.sku || x.item.name).join(", ") : "";
            },
            cell: labelsCell,
          },
        ]
      : []),
    {
      key: "confirmed",
      header: "Confirmed",
      headerTitle: `Whether ${who} confirmed having the revision on its page`,
      width: "minmax(170px,1fr)",
      value: (r) => info(r).s?.confirmedAt ?? "",
      text: (r) => confirmedText(info(r).s),
      cell: (r) => {
        const { s } = info(r);
        if (!s?.vendorRevision) return null;
        return s.confirmedAt ? (
          <span className="flex min-w-0 items-center gap-1 text-ok" title={confirmedText(s)}>
            <CircleCheck className="size-3.5 shrink-0" /> <span className="truncate">{confirmedText(s)}</span>
          </span>
        ) : (
          <span className="text-faint">Not confirmed yet</span>
        );
      },
      groupCell: (rs) => {
        const on = rs.filter((r) => info(r).s?.vendorRevision);
        if (!on.length) return null;
        const done = on.filter((r) => info(r).s?.confirmedAt).length;
        return done === on.length ? (
          <span className="flex items-center gap-1 font-normal text-ok">
            <CircleCheck className="size-3.5 shrink-0" /> All confirmed
          </span>
        ) : (
          <span className="font-normal text-faint">
            {done} of {on.length} confirmed
          </span>
        );
      },
    },
  ];

  const staff = can("doc_link.regenerate");
  const expiry = newest?.expires_at
    ? `${state === "expired" ? "Expired" : "Works until"} ${new Date(newest.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${DOC_LINK_DAYS} days after the PO was ${po.status === "cancelled" ? "cancelled" : "closed"}).`
    : `Works until ${DOC_LINK_DAYS} days after the PO is closed or cancelled.`;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <section className="flex flex-wrap items-start gap-3 border-b border-line px-4 py-3">
        <Link2 className="mt-0.5 size-4 shrink-0 text-faint" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">Documents link</span>
            {state && <Badge tone={STATE_LABEL[state][1]}>{STATE_LABEL[state][0]}</Badge>}
          </div>
          {po.sent_revision === null ? (
            <div className="text-[12px] text-muted">Made when the PO is marked as sent. It's printed on the PDF as a QR code and short link.</div>
          ) : !newest ? (
            <div className="text-[12px] text-muted">This PO was sent before documents links existed.</div>
          ) : (
            <>
              {url ? (
                <div className="mt-1 truncate font-mono text-[12px] select-all" title={url}>
                  {url}
                </div>
              ) : state === "live" ? (
                <div className="mt-1 text-[12px] text-faint">Loading the link…</div>
              ) : null}
              <div className="mt-0.5 text-[12px] text-muted">
                {state === "revoked"
                  ? `Turned off ${when(newest.revoked_at ?? "")}. ${who} sees "no longer active".`
                  : `${expiry} ${who} opens it without an account; it shows only this PO as last marked as sent.`}
              </div>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {url && (
            <>
              <Button size="sm" variant="primary" icon={<Copy className="size-3.5" />} onClick={copy}>
                Copy
              </Button>
              <Button size="sm" icon={<ExternalLink className="size-3.5" />} onClick={() => window.open(url, "_blank", "noopener")}>
                Open
              </Button>
            </>
          )}
          {staff && po.sent_revision !== null && (
            <Button size="sm" icon={<RefreshCw className="size-3.5" />} onClick={regenerate} title="A new link; the old one stops working">
              {live ? "Regenerate" : "Make a new link"}
            </Button>
          )}
          {staff && live && (
            <Button size="sm" variant="ghost" icon={<Link2Off className="size-3.5" />} onClick={revoke}>
              Revoke
            </Button>
          )}
        </div>
      </section>

      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">Lines</h3>
      </div>
      {sent && po.revision !== sent.revision && (
        <div className="mx-4 mb-1 text-[12px] text-warn">
          {who}'s page shows rev {sent.revision} as sent. Changes since then
          {removed ? ` (and ${removed} removed ${removed === 1 ? "line" : "lines"})` : ""} show when rev {po.revision} is marked as sent.
        </div>
      )}
      <div className="mx-4 mb-2 text-[12px]">
        <DataTable
          fit={20}
          rows={rows}
          columns={columns}
          defaultSort={{ key: "pos" }}
          sectionOf={!hasParts && packed.length ? (r) => (packing.has(r.line.id) ? PACKAGING : null) : undefined}
          groupBy={hasParts ? (r) => r.group : undefined}
          groupNoun={["line", "lines"]}
          rowNoun={hasParts ? ["row", "rows"] : ["line", "lines"]}
          exportName="PO documents"
          pageFilters={false}
          empty={<div className="p-4 text-center text-muted">No lines.</div>}
        />
      </div>

      <h3 className="px-4 pt-3 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase">Views and downloads {access.length || ""}</h3>
      {access.length === 0 ? (
        <div className="px-4 pb-4 text-[12px] text-muted">{po.sent_revision === null ? "Nothing yet." : `${who} hasn't opened the link yet.`}</div>
      ) : (
        <ol className="flex flex-col gap-1.5 px-4 pb-4 text-[12px]">
          {access.map((a) => {
            const d = byId.get(a.id);
            const Icon = a.kind === "download" ? Download : a.kind === "ack" ? CircleCheck : a.kind === "label" ? Tag : Eye;
            return (
              <li key={a.id} className="flex items-baseline gap-2">
                <Icon className="size-3.5 shrink-0 translate-y-0.5 text-faint" />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{who}</span>{" "}
                  {a.kind === "view"
                    ? "opened the documents page"
                    : a.kind === "download"
                      ? `downloaded ${fileName(a.file_version_id)} (Rev ${revName(a.spec_revision_id)})`
                      : a.kind === "label"
                        ? labelDownload(a) // Product labels ---
                        : `confirmed having Rev ${revName(a.spec_revision_id)}`}
                  {a.doc_link_id !== live?.id && <span className="text-faint"> · old link</span>}
                  {d && (d.ip || d.user_agent) && <span className="text-faint"> · {[d.ip, browser(d.user_agent)].filter(Boolean).join(", ")}</span>}
                </span>
                <span className="shrink-0 text-faint">{when(a.at)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
